import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  ScoutDataChangeStatus,
  type ScoutConversationMessage,
  type ScoutConversationMessagePart,
} from "generated-graphql/graphql";

import type { StreamStatus } from "../../../../stream";
import type { StreamEvent } from "../../../../stream/streamIterator";
import type {
  ScoutActiveTurn,
  ScoutChatMessage,
  ScoutDataChangeSummary,
  ScoutFilePart,
  ScoutMessagePart,
  ScoutReasoningPart,
  ScoutSourcePart,
  ScoutStepPart,
  ScoutTextPart,
  ScoutThreadSummary,
  ScoutToolPart,
  ScoutToolState,
  ScoutTurnStatus,
} from "./types";

import { stripProviderCitationArtifacts } from "../textSanitization";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "./scoutWriteMode";

type MessagesByConversationId = Record<string, ScoutChatMessage[]>;
type ConversationsById = Record<string, ScoutThreadSummary>;
type ActiveTurnsByConversationId = Partial<Record<string, ScoutActiveTurn>>;
type StreamStatusByConversationId = Partial<Record<string, StreamStatus>>;
type ConversationTitleSource = "fallback" | "model" | "user";

export interface ScoutConversationSnapshot {
  conversationsById: ConversationsById;
  messagesByConversationId: MessagesByConversationId;
  activeTurnsByConversationId: ActiveTurnsByConversationId;
  streamStatusByConversationId: StreamStatusByConversationId;
}

const EMPTY_SNAPSHOT: ScoutConversationSnapshot = {
  conversationsById: {},
  messagesByConversationId: {},
  activeTurnsByConversationId: {},
  streamStatusByConversationId: {},
};

const TERMINAL_ERROR_PART_ID_PREFIX = "terminal-error-";
const TERMINAL_SCOUT_EVENTS = new Set(["finish", "error", "abort", "turn_aborted"]);

/**
 * A `finish` with `finishReason: "tool-calls"` marks the end of one roundtrip,
 * not the end of the turn, so it must not tear down the SSE subscription.
 *
 * The output-token-limit family ("length" / raw "max_tokens") is likewise
 * never the end of the turn: the Scout runtime either rolls the truncated
 * roundtrip back and retries (turn continues streaming), or — when its
 * recovery budget is exhausted — follows up with a terminal `error` event
 * that settles the turn properly. Treating it as terminal here is what froze
 * the UI mid-turn while the server kept working: the client settled the turn,
 * detached the stream, and unlocked the composer on a turn still in flight.
 * Newer servers rewrite these finishes to "tool-calls" before they reach the
 * stream; this guard covers replays and older stream writers.
 */
function isTerminalStreamEvent(event: StreamEvent): boolean {
  if (!TERMINAL_SCOUT_EVENTS.has(event.event)) {
    return false;
  }
  if (event.event !== "finish") {
    return true;
  }
  const part = readStreamPart(event);
  const finishReason = part ? readOptionalString(part, "finishReason") : undefined;
  const rawFinishReason = part ? readOptionalString(part, "rawFinishReason") : undefined;
  if (finishReason === "tool-calls") {
    return false;
  }
  if (
    finishReason === "length" ||
    rawFinishReason === "length" ||
    rawFinishReason === "max_tokens"
  ) {
    return false;
  }
  return true;
}
const STREAMING_SCOUT_EVENTS = new Set([
  "start",
  "start-step",
  "finish-step",
  "text-start",
  "text-delta",
  "text-end",
  "reasoning-start",
  "reasoning-delta",
  "reasoning-end",
  "tool-input-start",
  "tool-input-delta",
  "tool-input-end",
  "tool-call",
  "tool-result",
  "tool-error",
  "source",
  "file",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readOptionalNumber(record: Record<string, unknown>, key: string): number | undefined {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readOptionalBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
  const value = record[key];
  return typeof value === "boolean" ? value : undefined;
}

function nextConversationSummary(
  existing: ScoutThreadSummary | undefined,
  patch: Partial<ScoutThreadSummary> & Pick<ScoutThreadSummary, "id">
): ScoutThreadSummary {
  return {
    ...(existing ?? { id: patch.id, writeMode: SCOUT_WRITE_MODE_GQL.OFF }),
    ...patch,
  };
}

function upsertDataChangeSummaries(
  dataChanges: ScoutDataChangeSummary[] | undefined,
  patches: ScoutDataChangeSummary[]
): ScoutDataChangeSummary[] {
  if (patches.length === 0) {
    return dataChanges ?? [];
  }

  const byId = new Map((dataChanges ?? []).map((change) => [change.id, change]));
  for (const patch of patches) {
    const current = byId.get(patch.id);
    byId.set(patch.id, mergeDataChangeSummary(current, patch));
  }

  const next: ScoutDataChangeSummary[] = [];
  const seen = new Set<string>();
  for (const change of dataChanges ?? []) {
    const updated = byId.get(change.id);
    if (!updated) continue;
    next.push(updated);
    seen.add(change.id);
  }
  for (const patch of patches) {
    if (seen.has(patch.id)) continue;
    const summary = byId.get(patch.id);
    if (!summary) continue;
    next.push(summary);
  }
  return next;
}

function mergeDataChangeStatus(
  current: ScoutDataChangeStatus,
  patch: ScoutDataChangeStatus
): ScoutDataChangeStatus {
  if (patch === ScoutDataChangeStatus.Applied && isTerminalDataChangeStatus(current)) {
    return current;
  }
  return patch;
}

function isTerminalDataChangeStatus(status: ScoutDataChangeStatus): boolean {
  return (
    status === ScoutDataChangeStatus.Undone ||
    status === ScoutDataChangeStatus.UndoFailed ||
    status === ScoutDataChangeStatus.Declined ||
    status === ScoutDataChangeStatus.Superseded
  );
}

function mergeDataChangeSummary(
  current: ScoutDataChangeSummary | undefined,
  patch: ScoutDataChangeSummary
): ScoutDataChangeSummary {
  if (!current) {
    return patch;
  }
  const status = mergeDataChangeStatus(current.status, patch.status);
  const refusedAppliedPatch = status !== patch.status;
  const next: ScoutDataChangeSummary = {
    ...current,
    status,
    undoable: refusedAppliedPatch ? current.undoable : patch.undoable,
  };
  if (!refusedAppliedPatch && patch.expiresAt !== undefined) {
    next.expiresAt = patch.expiresAt;
  }
  if (patch.proposeTurnId !== undefined) next.proposeTurnId = patch.proposeTurnId;
  if (patch.summary !== undefined) next.summary = patch.summary;
  if (patch.mutationRootField !== undefined) next.mutationRootField = patch.mutationRootField;
  if (patch.diff !== undefined) next.diff = patch.diff;
  if (patch.impact !== undefined) next.impact = patch.impact;
  if (patch.items !== undefined) next.items = patch.items;
  if (patch.operation !== undefined) next.operation = patch.operation;
  return next;
}

function expiresAtIsoFromEpochMs(value: number | undefined): string | null {
  if (value === undefined) return null;
  const iso = new Date(value).toISOString();
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

function assignStreamCardFields(
  summary: ScoutDataChangeSummary,
  item: Record<string, unknown>
): void {
  if ("proposeTurnId" in item) {
    const proposeTurnId = item.proposeTurnId;
    if (proposeTurnId === null || proposeTurnId === "") {
      summary.proposeTurnId = null;
    } else if (typeof proposeTurnId === "string") {
      summary.proposeTurnId = proposeTurnId;
    }
  }
  const cardSummary = readOptionalString(item, "summary");
  if (cardSummary !== undefined) {
    summary.summary = cardSummary;
  }
  const mutationRootField = readOptionalString(item, "mutationRootField");
  if (mutationRootField !== undefined) {
    summary.mutationRootField = mutationRootField;
  }
  if ("diff" in item) {
    summary.diff = item.diff;
  }
  if ("impact" in item) {
    summary.impact = item.impact;
  }
  if ("items" in item) {
    summary.items = item.items;
  }
  if ("operation" in item) {
    summary.operation = item.operation;
  }
}

function readStreamDataChangeSummaries(data: Record<string, unknown>): ScoutDataChangeSummary[] {
  const raw = data.changes;
  if (Array.isArray(raw)) {
    const summaries: ScoutDataChangeSummary[] = [];
    for (const item of raw) {
      if (!isRecord(item)) continue;
      const id = readOptionalString(item, "changeId");
      const status = parseScoutDataChangeStatus(readOptionalString(item, "status"));
      const undoable = readOptionalBoolean(item, "undoable");
      if (!id || !status || undoable === undefined) continue;
      const summary: ScoutDataChangeSummary = {
        id,
        status,
        undoable,
        expiresAt: expiresAtIsoFromEpochMs(readOptionalNumber(item, "expiresAt")),
      };
      assignStreamCardFields(summary, item);
      summaries.push(summary);
    }
    return summaries;
  }

  // Legacy single-id payload from in-flight stream entries.
  const id = readOptionalString(data, "changeId");
  const status = parseScoutDataChangeStatus(readOptionalString(data, "status"));
  if (!id || !status) {
    return [];
  }
  const pendingUndo = isRecord(data.pendingUndo) ? data.pendingUndo : null;
  return [
    {
      id,
      status,
      undoable: pendingUndo !== null,
      expiresAt: expiresAtIsoFromEpochMs(
        pendingUndo ? readOptionalNumber(pendingUndo, "expiresAt") : undefined
      ),
    },
  ];
}

function parseScoutDataChangeStatus(value: string | undefined): ScoutDataChangeStatus | undefined {
  switch (value) {
    case ScoutDataChangeStatus.AwaitingConfirmation:
    case ScoutDataChangeStatus.Confirmed:
    case ScoutDataChangeStatus.Declined:
    case ScoutDataChangeStatus.Applied:
    case ScoutDataChangeStatus.Undone:
    case ScoutDataChangeStatus.Superseded:
    case ScoutDataChangeStatus.ApplyFailed:
    case ScoutDataChangeStatus.UndoFailed:
      return value;
    default:
      return undefined;
  }
}

function readStreamDataRecord(event: StreamEvent): Record<string, unknown> | undefined {
  return isRecord(event.data) ? event.data : undefined;
}

function readStreamPart(event: StreamEvent): Record<string, unknown> | undefined {
  const data = readStreamDataRecord(event);
  if (!data) {
    return undefined;
  }

  return isRecord(data.part) ? data.part : undefined;
}

function readStreamConversationId(event: StreamEvent): string | undefined {
  const data = readStreamDataRecord(event);
  return data ? readOptionalString(data, "conversationId") : undefined;
}

function readStreamRequestId(event: StreamEvent): string | undefined {
  const data = readStreamDataRecord(event);
  return data ? readOptionalString(data, "requestId") : undefined;
}

function readStreamCommandId(event: StreamEvent): string | undefined {
  const data = readStreamDataRecord(event);
  return data ? readOptionalString(data, "commandId") : undefined;
}

function readErrorMessage(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  if (isRecord(value)) {
    return readOptionalString(value, "message");
  }

  return undefined;
}

function readStreamError(event: StreamEvent): string | undefined {
  const data = readStreamDataRecord(event);
  if (!data) {
    return undefined;
  }

  const topLevel = readErrorMessage(data.error) ?? readErrorMessage(data.reason);
  if (topLevel) {
    return topLevel;
  }

  // Runtime `error` events carry the message at `data.part.error.message`
  // (the canonical Scout runtime stream-event error shape). The top-level
  // `error` / `reason` fields cover older / non-runtime events.
  const part = isRecord(data.part) ? data.part : undefined;
  if (part) {
    return readErrorMessage(part.error);
  }

  return undefined;
}

function readStreamEventTimestampMs(eventId: string): number | undefined {
  return safeParseInt(eventId.split("-")[0]) ?? undefined;
}

function readJsonLikeValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
}

function parseJsonIfPossible(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function readTextDelta(part: Record<string, unknown>): string {
  return readOptionalString(part, "textDelta") ?? readOptionalString(part, "text") ?? "";
}

function readReasoningDelta(part: Record<string, unknown>): string {
  return readOptionalString(part, "textDelta") ?? readOptionalString(part, "text") ?? "";
}

function resolveStreamPartId(requestId: string, kind: string, part: Record<string, unknown>) {
  return (
    readOptionalString(part, "id") ??
    readOptionalString(part, "toolCallId") ??
    `scout-${requestId}-${kind}`
  );
}

function resolveToolCallId(requestId: string, part: Record<string, unknown>) {
  return (
    readOptionalString(part, "toolCallId") ??
    readOptionalString(part, "id") ??
    `scout-${requestId}-tool`
  );
}

function normalizeRole(role: string): ScoutChatMessage["role"] | null {
  if (role === "compaction") {
    // Durable fence rows are model-input only; hide from customer chat.
    return null;
  }
  if (role === "system" || role === "user" || role === "assistant") {
    return role;
  }

  return "assistant";
}

function textFromPart(part: ScoutMessagePart): string {
  if (isTerminalScoutErrorPart(part)) {
    return "";
  }

  if (part.type === "text" || part.type === "reasoning") {
    return stripProviderCitationArtifacts(part.text);
  }

  return "";
}

function isTerminalScoutErrorPart(part: ScoutMessagePart): boolean {
  return (
    part.type === "text" &&
    part.agentState === "error" &&
    part.id.startsWith(TERMINAL_ERROR_PART_ID_PREFIX)
  );
}

/**
 * External store for render-facing Scout conversation state.
 *
 * This class is intentionally reducer-like: it accepts persisted history and raw
 * stream events, then produces stable UI state for conversations, messages,
 * active turns, and stream status. It does not own network lifecycle or query
 * orchestration; that lives in ScoutConversationController.
 */
export class ScoutConversationStore {
  private snapshot: ScoutConversationSnapshot = EMPTY_SNAPSHOT;
  private readonly listeners = new Set<() => void>();
  private readonly titleSourceByConversationId: Partial<Record<string, ConversationTitleSource>> =
    {};
  private batchDepth = 0;
  private hasPendingNotification = false;

  // Per-turn bookkeeping for text/reasoning block ids.
  //
  // The matching server-side path (encamp-scout-connection's
  // ScoutConversationMaterializer) flushes its buffer + deletes its id key on
  // each text-end / reasoning-end. That means when the upstream SDK reuses
  // the same logical id for a later block in the same turn (which Anthropic
  // and a couple of agent wrappers do), the SERVER cleanly allocates a fresh
  // buffer and writes a new DB row with a fresh seq. The CLIENT used to key
  // parts by SDK id directly via `upsertPart`, which merged the second block
  // into the first and pinned tool calls below it. We mirror the server's
  // shape by giving every *-start its own fabricated client id and remapping
  // subsequent deltas/end through `openByRawId`. Closing a block (text-end /
  // reasoning-end) drops the map entry so a later *-start with the same raw
  // id allocates a new fabricated id.
  //
  //   latestId    — the most-recently-opened id per kind, used as a fallback
  //                 when a delta/end arrives without an explicit raw id.
  //   nextSeq     — monotonic counter feeding fabricated ids
  //                 (`scout-{requestId}-{kind}-{seq}`).
  //   openByRawId — raw SDK id → fabricated client id, scoped to the
  //                 currently open block. Cleared on the matching *-end.
  private readonly streamBlockState: Map<
    string,
    {
      latestId: { text: string | null; reasoning: string | null };
      nextSeq: { text: number; reasoning: number };
      openByRawId: { text: Map<string, string>; reasoning: Map<string, string> };
    }
  > = new Map();

  public subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  public getSnapshot = () => this.snapshot;

  /** Applies a user-renamed title and marks the source so SSE auto-titles won't overwrite it. */
  public setConversationTitle(input: { conversationId: string; title: string }) {
    const title = input.title.trim();
    if (!title) {
      return;
    }

    const existingConversation = this.snapshot.conversationsById[input.conversationId];
    if (existingConversation?.title === title) {
      this.titleSourceByConversationId[input.conversationId] = "user";
      return;
    }

    this.titleSourceByConversationId[input.conversationId] = "user";
    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [input.conversationId]: nextConversationSummary(existingConversation, {
          id: input.conversationId,
          title,
        }),
      },
    });
  }

  /** Refreshes extraction status labels on file parts (used while large PDFs extract). */
  public updatePrivateDocumentStatuses(input: {
    conversationId: string;
    statuses: Array<{
      id: string;
      ingestionMode?: string;
      extractionStatus?: string;
      pageCount?: number | null;
      thumbnailSignedUrl?: string | null;
      extractionProgress?: {
        pagesCompleted: number;
        pagesTotal?: number | null;
      } | null;
    }>;
  }) {
    const messages = this.snapshot.messagesByConversationId[input.conversationId];
    if (!messages || messages.length === 0) {
      return;
    }

    const statusById = new Map(input.statuses.map((status) => [status.id, status]));
    let changed = false;

    const nextMessages = messages.map((message) => {
      let messageChanged = false;
      const nextParts = message.parts.map((part) => {
        if (part.type !== "file" || !part.url?.startsWith("encamp-private-document://")) {
          return part;
        }

        const privateDocumentId = part.url.slice("encamp-private-document://".length);
        const status = statusById.get(privateDocumentId);
        if (!status) {
          return part;
        }

        const nextIngestionMode = status.ingestionMode ?? part.ingestionMode;
        const nextExtractionStatus = status.extractionStatus ?? part.extractionStatus;
        const nextExtractionProgress = status.extractionProgress ?? part.extractionProgress;
        const nextPageCount =
          status.pageCount !== undefined ? status.pageCount : part.document?.pageCount;
        const nextThumbnailSignedUrl =
          status.thumbnailSignedUrl !== undefined
            ? status.thumbnailSignedUrl
            : part.document?.thumbnailSignedUrl;
        const progressUnchanged =
          nextExtractionProgress?.pagesCompleted === part.extractionProgress?.pagesCompleted &&
          nextExtractionProgress?.pagesTotal === part.extractionProgress?.pagesTotal;
        const pageCountUnchanged = nextPageCount === part.document?.pageCount;
        const thumbnailUnchanged = nextThumbnailSignedUrl === part.document?.thumbnailSignedUrl;
        if (
          nextIngestionMode === part.ingestionMode &&
          nextExtractionStatus === part.extractionStatus &&
          progressUnchanged &&
          pageCountUnchanged &&
          thumbnailUnchanged
        ) {
          return part;
        }

        messageChanged = true;
        return {
          ...part,
          ingestionMode: nextIngestionMode,
          extractionStatus: nextExtractionStatus,
          extractionProgress: nextExtractionProgress,
          document: part.document
            ? {
                ...part.document,
                pageCount: nextPageCount,
                thumbnailSignedUrl: nextThumbnailSignedUrl,
              }
            : nextPageCount !== undefined || nextThumbnailSignedUrl !== undefined
              ? {
                  id: privateDocumentId,
                  pageCount: nextPageCount,
                  thumbnailSignedUrl: nextThumbnailSignedUrl,
                }
              : undefined,
        };
      });

      if (!messageChanged) {
        return message;
      }

      changed = true;
      return {
        ...message,
        parts: nextParts,
      };
    });

    if (!changed) {
      return;
    }

    this.setSnapshot({
      ...this.snapshot,
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: nextMessages,
      },
    });
  }

  public batch = <T>(callback: () => T): T => {
    this.batchDepth += 1;

    try {
      return callback();
    } finally {
      this.batchDepth -= 1;

      if (this.batchDepth === 0 && this.hasPendingNotification) {
        this.hasPendingNotification = false;

        for (const listener of this.listeners) {
          listener();
        }
      }
    }
  };

  // Merges persisted backend history into local chat state while preserving the
  // right optimistic/streaming fragments until the server has fully caught up.
  public ingestHistory(input: {
    tenantId: string;
    conversationId: string;
    title?: string | null;
    writeMode?: ScoutWriteModeGql;
    dataChanges?: ScoutDataChangeSummary[];
    messages: ScoutConversationMessage[];
    keepActiveAssistantStreaming?: boolean;
  }) {
    const activeTurn = this.snapshot.activeTurnsByConversationId[input.conversationId];
    const activeAssistantMessageId = activeTurn ? `scout-assistant-${activeTurn.requestId}` : null;
    /**
     * When the active assistant response appears in persisted history before the
     * local stream has finished reconciling, keep it marked as streaming so the UI
     * continues treating it like the active turn instead of briefly treating it as
     * if it has finished streaming.
     */
    const existingMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    const existingMessagesById = new Map(existingMessages.map((message) => [message.id, message]));
    const historyMessages = input.messages.flatMap((message) => {
      const mappedMessage = this.mapHistoryMessage(message);
      if (!mappedMessage) {
        return [];
      }
      if (
        input.keepActiveAssistantStreaming === true &&
        activeAssistantMessageId &&
        mappedMessage.id === activeAssistantMessageId &&
        mappedMessage.role === "assistant"
      ) {
        return [
          {
            ...mappedMessage,
            isStreaming: true,
          },
        ];
      }

      // Preserve the already-rendered assistant content when a forced network
      // refetch returns a blank/leaner row. DB materialization can lag the live
      // stream, so a `network-only` re-ingest right after a turn (e.g. on
      // Retry) can momentarily hand back an empty assistant message; letting it
      // win blanks the previous answer to an empty/"Thinking…" bubble until the
      // next refetch. Keep the local value while the DB catches up (ENG-15662).
      const existing = existingMessagesById.get(mappedMessage.id);
      if (
        mappedMessage.role === "assistant" &&
        existing &&
        !this.messageHasRenderableContent(mappedMessage) &&
        this.messageHasRenderableContent(existing)
      ) {
        return [existing];
      }

      return [mappedMessage];
    });
    const historyMessageIds = new Set(historyMessages.map((message) => message.id));
    const hasHistoryAssistantMessage = historyMessages.some(
      (message) => message.role === "assistant"
    );
    const liveAssistantMessageId = activeTurn
      ? `scout-assistant-${activeTurn.requestId}`
      : undefined;
    const hasHistoryAssistantFromActiveTurn = historyMessages.some(
      (message) => message.role === "assistant" && this.isMessageFromTurn(message, activeTurn)
    );
    const localMessagesToPreserve = existingMessages.filter((message) => {
      if (historyMessageIds.has(message.id)) {
        return false;
      }

      if (this.isTerminalTurn(activeTurn) && this.isMessageFromTurn(message, activeTurn)) {
        return message.role !== "assistant" || !hasHistoryAssistantFromActiveTurn;
      }

      if (message.id === liveAssistantMessageId && !hasHistoryAssistantFromActiveTurn) {
        return true;
      }

      return (
        message.isStreaming === true &&
        (!hasHistoryAssistantMessage || message.role !== "assistant")
      );
    });
    const messages = [...historyMessages, ...localMessagesToPreserve];
    const lastMessage = messages.at(-1);
    const existingConversation = this.snapshot.conversationsById[input.conversationId];
    const persistedTitle = input.title?.trim();
    const existingTitle = existingConversation?.title?.trim();
    let title: string | undefined;
    if (persistedTitle) {
      title = persistedTitle;
      this.titleSourceByConversationId[input.conversationId] = "model";
    } else if (existingTitle) {
      title = existingTitle;
    }

    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [input.conversationId]: {
          id: input.conversationId,
          tenantId: input.tenantId,
          title,
          updatedAt: lastMessage?.updatedAt,
          writeMode: input.writeMode ?? existingConversation?.writeMode ?? SCOUT_WRITE_MODE_GQL.OFF,
          dataChanges:
            input.dataChanges !== undefined
              ? upsertDataChangeSummaries(existingConversation?.dataChanges, input.dataChanges)
              : existingConversation?.dataChanges,
        },
      },
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: messages,
      },
    });
  }

  /**
   * Patch conversation overlay rows by id (undo mutation result and SSE
   * `data_change_undo_updated`). Does not create cards; UI collects those
   * from execute_graphql tool parts.
   */
  public patchConversationDataChanges(
    conversationId: string,
    patches: ScoutDataChangeSummary[]
  ): void {
    this.applyDataChangeSummaryPatches(conversationId, patches);
  }

  // Prepends an older persisted history page (for infinite upward scroll).
  public prependHistoryPage(input: {
    tenantId: string;
    conversationId: string;
    title?: string | null;
    writeMode?: ScoutWriteModeGql;
    messages: ScoutConversationMessage[];
  }) {
    const existingMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    if (existingMessages.length === 0) {
      this.ingestHistory(input);
      return;
    }

    const olderMessages = input.messages
      .map((message) => this.mapHistoryMessage(message))
      .filter((message): message is ScoutChatMessage => message != null);
    const existingIds = new Set(existingMessages.map((message) => message.id));
    const dedupedOlderMessages = olderMessages.filter((message) => !existingIds.has(message.id));
    const mergedMessages = [...dedupedOlderMessages, ...existingMessages];
    const existingConversation = this.snapshot.conversationsById[input.conversationId];
    const persistedTitle = input.title?.trim();
    const existingTitle = existingConversation?.title?.trim();
    const title = persistedTitle || existingTitle;

    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [input.conversationId]: nextConversationSummary(existingConversation, {
          id: input.conversationId,
          tenantId: input.tenantId,
          title,
          updatedAt: existingConversation?.updatedAt ?? mergedMessages.at(-1)?.updatedAt,
          writeMode: input.writeMode ?? existingConversation?.writeMode ?? SCOUT_WRITE_MODE_GQL.OFF,
        }),
      },
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: mergedMessages,
      },
    });
  }

  // Used for non-streaming local inserts. Most active-turn startup goes through
  // startOptimisticTurn instead.
  public appendUserMessage(input: {
    tenantId: string;
    conversationId: string;
    messageId: string;
    content: string;
  }) {
    const previousMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    const existing = previousMessages.some((message) => message.id === input.messageId);
    const userMessage: ScoutChatMessage = {
      id: input.messageId,
      clientInstanceId: input.messageId,
      persistedMessageId: input.messageId,
      role: "user",
      content: input.content,
      parts: [
        {
          id: `${input.messageId}-text`,
          type: "text",
          text: input.content,
        },
      ],
      isStreaming: false,
    };
    const messages = existing ? previousMessages : [...previousMessages, userMessage];

    const existingConversation = this.snapshot.conversationsById[input.conversationId];
    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [input.conversationId]: nextConversationSummary(existingConversation, {
          id: input.conversationId,
          tenantId: input.tenantId,
          updatedAt: new Date().toISOString(),
        }),
      },
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: messages,
      },
    });
  }

  // Creates the local user message and temporary assistant "Thinking..." message
  // before sendMessage returns real ids from the backend.
  public startOptimisticTurn(input: {
    tenantId: string;
    conversationId: string;
    userMessageId: string;
    requestId: string;
    content: string;
    writeMode?: ScoutWriteModeGql;
    attachments?: Array<{
      privateDocumentId: string;
      filename: string;
      mediaType: string;
      sizeBytes?: number;
      signedDownloadUrl?: string;
      thumbnailSignedUrl?: string;
      pageCount?: number | null;
      ingestionMode?: string;
      extractionStatus?: string;
      filePartUrl?: string;
    }>;
  }) {
    const previousMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    const startedAtMs = Date.now();
    const now = new Date(startedAtMs).toISOString();
    const userMessage: ScoutChatMessage = {
      id: input.userMessageId,
      clientInstanceId: input.userMessageId,
      persistedMessageId: input.userMessageId,
      role: "user",
      content: input.content,
      parts: [
        {
          id: `${input.userMessageId}-text`,
          type: "text",
          text: input.content,
        },
        ...(input.attachments ?? []).map((attachment, index) => ({
          id: `${input.userMessageId}-file-${index}`,
          type: "file" as const,
          filename: attachment.filename,
          title: attachment.filename,
          mediaType: attachment.mediaType,
          url:
            attachment.filePartUrl ?? `encamp-private-document://${attachment.privateDocumentId}`,
          ingestionMode: attachment.ingestionMode,
          extractionStatus: attachment.extractionStatus,
          document:
            attachment.signedDownloadUrl ||
            attachment.thumbnailSignedUrl ||
            attachment.sizeBytes !== undefined ||
            attachment.pageCount !== undefined
              ? {
                  id: attachment.privateDocumentId,
                  signedDownloadUrl: attachment.signedDownloadUrl,
                  thumbnailSignedUrl: attachment.thumbnailSignedUrl,
                  sizeBytes: attachment.sizeBytes,
                  pageCount: attachment.pageCount,
                }
              : undefined,
        })),
      ],
      isStreaming: false,
      createdAt: now,
      updatedAt: now,
    };
    const assistantMessage: ScoutChatMessage = {
      id: `scout-assistant-${input.requestId}`,
      role: "assistant",
      content: "",
      parts: [
        {
          id: `scout-assistant-${input.requestId}-thinking`,
          type: "text",
          text: "Thinking...",
          agentState: "thinking",
        },
      ],
      isStreaming: true,
      createdAt: now,
      updatedAt: now,
    };
    const messages = [
      ...previousMessages.filter(
        (message) => message.id !== userMessage.id && message.id !== assistantMessage.id
      ),
      userMessage,
      assistantMessage,
    ];
    const existingConversation = this.snapshot.conversationsById[input.conversationId];

    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [input.conversationId]: nextConversationSummary(existingConversation, {
          id: input.conversationId,
          tenantId: input.tenantId,
          updatedAt: now,
          writeMode: input.writeMode ?? existingConversation?.writeMode ?? SCOUT_WRITE_MODE_GQL.OFF,
        }),
      },
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: messages,
      },
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: "starting",
          startedAtMs,
        },
      },
    });
  }

  // Replaces optimistic ids with the persisted conversation/message/request ids
  // while keeping the in-flight local turn visible.
  public reconcileOptimisticTurn(input: {
    tenantId: string;
    optimisticConversationId: string;
    conversationId: string;
    optimisticUserMessageId: string;
    userMessageId: string;
    optimisticRequestId: string;
    requestId: string;
    content: string;
    title?: string | null;
    writeMode?: ScoutWriteModeGql;
  }) {
    const optimisticMessages =
      this.snapshot.messagesByConversationId[input.optimisticConversationId] ?? [];
    const reconciledMessages = optimisticMessages.map((message) => {
      if (message.id === input.optimisticUserMessageId) {
        return {
          ...message,
          id: input.userMessageId,
          clientInstanceId: message.clientInstanceId ?? input.optimisticUserMessageId,
          persistedMessageId: input.userMessageId,
          parts: message.parts?.map((part) =>
            part.id === `${input.optimisticUserMessageId}-text`
              ? { ...part, id: `${input.userMessageId}-text` }
              : part
          ),
        };
      }

      if (message.id === `scout-assistant-${input.optimisticRequestId}`) {
        return {
          ...message,
          id: `scout-assistant-${input.requestId}`,
          parts: message.parts?.map((part) =>
            part.id === `scout-assistant-${input.optimisticRequestId}-thinking`
              ? { ...part, id: `scout-assistant-${input.requestId}-thinking` }
              : part
          ),
        };
      }

      return message;
    });
    const existingTargetMessages =
      input.optimisticConversationId === input.conversationId
        ? []
        : (this.snapshot.messagesByConversationId[input.conversationId] ?? []);
    const reconciledMessageIds = new Set(reconciledMessages.map((message) => message.id));
    const nextTargetMessages = [
      ...existingTargetMessages.filter((message) => !reconciledMessageIds.has(message.id)),
      ...reconciledMessages,
    ];
    const { [input.optimisticConversationId]: _oldConversation, ...remainingConversationsById } =
      this.snapshot.conversationsById;
    const { [input.optimisticConversationId]: _oldMessages, ...remainingMessagesByConversationId } =
      this.snapshot.messagesByConversationId;
    const { [input.optimisticConversationId]: _oldTurn, ...remainingActiveTurnsByConversationId } =
      this.snapshot.activeTurnsByConversationId;
    const optimisticTurn =
      this.snapshot.activeTurnsByConversationId[input.optimisticConversationId];
    const optimisticTitleSource = this.titleSourceByConversationId[input.optimisticConversationId];
    const existingConversation = this.snapshot.conversationsById[input.conversationId];
    const apiTitle = input.title?.trim();
    const resolvedTitle = apiTitle || existingConversation?.title;
    if (optimisticTitleSource) {
      this.titleSourceByConversationId[input.conversationId] = optimisticTitleSource;
      if (input.optimisticConversationId !== input.conversationId) {
        delete this.titleSourceByConversationId[input.optimisticConversationId];
      }
    }

    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...remainingConversationsById,
        [input.conversationId]: nextConversationSummary(existingConversation ?? _oldConversation, {
          id: input.conversationId,
          tenantId: input.tenantId,
          title: resolvedTitle,
          updatedAt: new Date().toISOString(),
          writeMode:
            input.writeMode ??
            existingConversation?.writeMode ??
            _oldConversation?.writeMode ??
            SCOUT_WRITE_MODE_GQL.OFF,
        }),
      },
      messagesByConversationId: {
        ...remainingMessagesByConversationId,
        [input.conversationId]: nextTargetMessages,
      },
      activeTurnsByConversationId: {
        ...remainingActiveTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: "streaming",
          startedAtMs: optimisticTurn?.startedAtMs ?? Date.now(),
        },
      },
    });
  }

  // Converts a failed optimistic turn into stable UI state.
  public markTurnError(input: {
    conversationId: string;
    requestId: string;
    error: string;
    couldNotStart?: boolean;
  }) {
    const previousMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    const nextMessages: ScoutChatMessage[] = previousMessages.map((message) =>
      message.id === `scout-assistant-${input.requestId}`
        ? {
            ...message,
            content: input.error,
            parts: [
              {
                id: `scout-assistant-${input.requestId}-error`,
                type: "text",
                text: input.error,
                agentState: "error",
              },
            ],
            isStreaming: false,
          }
        : message
    );

    this.setSnapshot({
      ...this.snapshot,
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: nextMessages,
      },
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: "error",
          startedAtMs: this.snapshot.activeTurnsByConversationId[input.conversationId]?.startedAtMs,
          error: input.error,
          couldNotStart: input.couldNotStart,
        },
      },
    });
  }

  // Used after an abort request is accepted locally but before Scout emits the
  // terminal abort event on the stream.
  public markTurnPending(input: { conversationId: string; requestId: string }) {
    this.setSnapshot({
      ...this.snapshot,
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: "streaming",
          startedAtMs:
            this.snapshot.activeTurnsByConversationId[input.conversationId]?.startedAtMs ??
            Date.now(),
        },
      },
    });
  }

  /**
   * Marks the active turn as aborted immediately after the backend accepts the abort request.
   *
   * We optimistically finalize the streaming assistant message so the UI can accept a new prompt
   * even if the terminal SSE abort event is delayed or missed.
   */
  public markTurnAborted(input: { conversationId: string; requestId: string }) {
    const previousMessages = this.snapshot.messagesByConversationId[input.conversationId] ?? [];
    const assistantId = `scout-assistant-${input.requestId}`;
    const nextMessages = previousMessages.map((message) => {
      if (message.id !== assistantId || message.role !== "assistant") {
        return message;
      }

      const withoutThinking = this.removeOptimisticThinkingParts(message);
      const parts = withoutThinking.parts ?? [];
      const hasVisiblePart = parts.some((part) => part.type === "text" || part.type === "tool");
      const nextParts: ScoutMessagePart[] = hasVisiblePart
        ? parts
        : [
            {
              id: `${assistantId}-aborted`,
              type: "text",
              text: "Aborted.",
              agentState: "idle",
            },
          ];

      return this.recomputeMessageContent({
        ...withoutThinking,
        parts: nextParts,
        isStreaming: false,
      });
    });

    this.setSnapshot({
      ...this.snapshot,
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [input.conversationId]: nextMessages,
      },
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: "aborted",
          startedAtMs:
            this.snapshot.activeTurnsByConversationId[input.conversationId]?.startedAtMs ??
            Date.now(),
        },
      },
    });
  }

  // Used when persisted history proves a locally active turn has already
  // completed even though the client never observed the terminal SSE event.
  public markTurnFinishedFromHistory(input: {
    conversationId: string;
    requestId: string;
    status?: Extract<ScoutTurnStatus, "finished" | "aborted" | "error">;
    lastEventId?: string;
  }) {
    const activeTurn = this.snapshot.activeTurnsByConversationId[input.conversationId];
    if (!this.isRunningTurn(activeTurn) || activeTurn.requestId !== input.requestId) {
      return;
    }

    this.setSnapshot({
      ...this.snapshot,
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [input.conversationId]: {
          conversationId: input.conversationId,
          requestId: input.requestId,
          status: input.status ?? "finished",
          startedAtMs: activeTurn.startedAtMs,
          lastEventId: input.lastEventId ?? activeTurn.lastEventId,
        },
      },
    });
  }

  // Mirrors transport status for UI affordances/debugging; it does not drive the
  // chat state machine by itself.
  public setStreamStatus(conversationId: string, status: StreamStatus) {
    if (this.snapshot.streamStatusByConversationId[conversationId] === status) {
      return;
    }

    this.setSnapshot({
      ...this.snapshot,
      streamStatusByConversationId: {
        ...this.snapshot.streamStatusByConversationId,
        [conversationId]: status,
      },
    });
  }

  // Single event reducer for live Scout SSE traffic. This is the core transition
  // point from raw stream events to renderable chat state.
  public applyStreamEvent(event: StreamEvent): boolean {
    const conversationId = readStreamConversationId(event);
    const requestId = readStreamRequestId(event);
    const commandId = readStreamCommandId(event);

    // The undo-affordance event is conversation-scoped, not turn-scoped, so it
    // carries no requestId — handle it before the requestId guard below.
    if (conversationId && event.event === "data_change_undo_updated") {
      return this.applyDataChangeUndoEvent(event, conversationId);
    }

    if (!conversationId || !requestId) {
      return false;
    }

    if (event.event === "raw") {
      return false;
    }

    if (event.event === "conversation_title_updated") {
      return this.applyConversationTitleEvent(event, conversationId);
    }

    const previousMessages = this.snapshot.messagesByConversationId[conversationId] ?? [];
    const activeTurn = this.snapshot.activeTurnsByConversationId[conversationId];
    // Once we observe a terminal state locally (finish/error/abort), ignore any
    // subsequent non-terminal stream events. These can arrive out of order or
    // after an abort request and would otherwise resurrect the UI into a
    // "streaming" state, re-disabling the input.
    if (this.isTerminalTurn(activeTurn) && !isTerminalStreamEvent(event)) {
      return false;
    }
    const hasLiveStreamingAssistant = previousMessages.some(
      (message) => message.role === "assistant" && message.isStreaming === true
    );
    if (!activeTurn && isTerminalStreamEvent(event) && !hasLiveStreamingAssistant) {
      return false;
    }

    if (
      this.shouldIgnoreMismatchedRunningTurnEvent({
        activeTurn,
        commandId,
        event,
        previousMessages,
        requestId,
      })
    ) {
      return false;
    }

    const liveMessage = this.getOrCreateLiveAssistantMessage({
      activeTurn,
      commandId,
      messages: previousMessages,
      requestId,
    });
    const previousLiveMessageId = liveMessage.id;
    const normalizedLiveMessage =
      liveMessage.id === `scout-assistant-${requestId}`
        ? liveMessage
        : {
            ...liveMessage,
            id: `scout-assistant-${requestId}`,
          };
    const shouldMaterializeMessage =
      STREAMING_SCOUT_EVENTS.has(event.event) || isTerminalStreamEvent(event);
    const nextLiveMessage = shouldMaterializeMessage
      ? this.updateMessageForStreamEvent(normalizedLiveMessage, requestId, event)
      : normalizedLiveMessage;
    if (isTerminalStreamEvent(event)) {
      // Drop per-turn stream-block bookkeeping. The map is keyed by requestId,
      // and the next turn will allocate fresh state on its first event. Done
      // after `updateMessageForStreamEvent` so any final delta still resolves
      // against the open block ids.
      this.streamBlockState.delete(requestId);
    }
    const replacedMessages = this.replaceMessage(
      previousMessages,
      nextLiveMessage,
      previousLiveMessageId
    );
    const nextMessageIsStreaming = !isTerminalStreamEvent(event);
    let didNormalizeStreamingFlag = false;
    const nextMessages = replacedMessages.map((message) => {
      if (message.id !== nextLiveMessage.id || message.isStreaming === nextMessageIsStreaming) {
        return message;
      }

      didNormalizeStreamingFlag = true;
      return {
        ...message,
        isStreaming: nextMessageIsStreaming,
      };
    });
    const normalizedMessages = didNormalizeStreamingFlag ? nextMessages : replacedMessages;

    const turnStatus = this.resolveTurnStatus(event);
    const error = event.event === "error" ? readStreamError(event) : undefined;
    const nextStartedAtMs = activeTurn?.startedAtMs;
    const didRenderRelevantTurnChange =
      activeTurn?.conversationId !== conversationId ||
      activeTurn?.requestId !== requestId ||
      activeTurn?.commandId !== commandId ||
      activeTurn?.status !== turnStatus ||
      activeTurn?.startedAtMs !== nextStartedAtMs ||
      activeTurn?.error !== error;
    const didMessageChange = normalizedMessages !== previousMessages;

    if (!didMessageChange && !didRenderRelevantTurnChange) {
      return isTerminalStreamEvent(event);
    }

    this.setSnapshot({
      ...this.snapshot,
      messagesByConversationId: {
        ...this.snapshot.messagesByConversationId,
        [conversationId]: normalizedMessages,
      },
      activeTurnsByConversationId: {
        ...this.snapshot.activeTurnsByConversationId,
        [conversationId]: {
          conversationId,
          requestId,
          commandId,
          status: turnStatus,
          startedAtMs: nextStartedAtMs,
          lastEventId: event.id,
          lastEventType: event.event,
          error,
        },
      },
    });

    return isTerminalStreamEvent(event);
  }

  private recomputeMessageContent(message: ScoutChatMessage): ScoutChatMessage {
    if (message.role === "user") {
      return message;
    }

    const content = (message.parts ?? []).map(textFromPart).filter(Boolean).join("");
    return {
      ...message,
      content,
    };
  }

  private applyConversationTitleEvent(event: StreamEvent, conversationId: string): boolean {
    const data = readStreamDataRecord(event);
    if (!data) {
      return false;
    }

    const title = readOptionalString(data, "title");
    const source = readOptionalString(data, "source");
    if (!title || (source !== "fallback" && source !== "model")) {
      return false;
    }

    const existingConversation = this.snapshot.conversationsById[conversationId];
    const existingTitle = existingConversation?.title?.trim();
    const existingSource = this.titleSourceByConversationId[conversationId];
    if (existingSource === "user") {
      return false;
    }
    if (
      source === "fallback" &&
      existingTitle &&
      existingTitle !== title &&
      existingSource !== "fallback"
    ) {
      return false;
    }

    if (existingTitle === title && existingSource === source) {
      return false;
    }

    this.titleSourceByConversationId[conversationId] = source;
    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [conversationId]: nextConversationSummary(existingConversation, {
          id: conversationId,
          title,
        }),
      },
    });

    return false;
  }

  private applyDataChangeUndoEvent(event: StreamEvent, conversationId: string): boolean {
    const data = readStreamDataRecord(event);
    if (!data) {
      return false;
    }

    this.applyDataChangeSummaryPatches(conversationId, readStreamDataChangeSummaries(data));
    return false;
  }

  private applyDataChangeSummaryPatches(
    conversationId: string,
    patches: ScoutDataChangeSummary[]
  ): void {
    if (patches.length === 0) {
      return;
    }

    const existing = this.snapshot.conversationsById[conversationId];
    this.setSnapshot({
      ...this.snapshot,
      conversationsById: {
        ...this.snapshot.conversationsById,
        [conversationId]: {
          ...(existing ?? { id: conversationId, writeMode: SCOUT_WRITE_MODE_GQL.OFF }),
          id: conversationId,
          dataChanges: upsertDataChangeSummaries(existing?.dataChanges, patches),
        },
      },
    });
  }

  private mapHistoryPart(part: ScoutConversationMessagePart): ScoutMessagePart {
    // Prefer the provider/runtime-assigned id so a part hydrated from history
    // shares its key with replayed stream events. The DB primary key is opaque
    // to the runtime, so the runtime never references it; the store only uses
    // `id` as a merge key in `upsertPart`. Falling back to `part.id` keeps
    // behavior unchanged for messages persisted before streamPartId existed.
    const id = part.streamPartId ?? part.id;
    if (part.type === "tool") {
      return {
        id,
        type: "tool",
        toolName: part.toolName ?? "unknown_tool",
        toolDisplayName: part.toolDisplayName ?? undefined,
        toolCallId: part.toolCallId ?? id,
        toolState: this.normalizeToolState(part.toolState),
        toolInput: part.toolInput ?? undefined,
        toolOutput: part.toolOutput ?? undefined,
        toolError: part.toolError ?? undefined,
        toolDurationMs: part.toolDurationMs ?? undefined,
        agentState: part.agentState ?? undefined,
        createdAt: part.createdAt ?? undefined,
      };
    }

    if (part.type === "abort") {
      return {
        id: part.id,
        type: "abort",
        text: part.text ?? null,
        agentState: part.agentState ?? undefined,
      };
    }

    if (part.type === "reasoning") {
      return {
        id,
        type: "reasoning",
        text: part.text ?? "",
        agentState: part.agentState ?? undefined,
      };
    }

    if (part.type === "source_url" || part.type === "source_document" || part.type === "source") {
      return {
        id,
        type: "source",
        sourceId: part.sourceId ?? undefined,
        url: part.url ?? undefined,
        title: part.title ?? undefined,
        mediaType: part.mediaType ?? undefined,
        filename: part.filename ?? undefined,
        agentState: part.agentState ?? undefined,
      };
    }

    if (part.type === "file") {
      return {
        id,
        type: "file",
        url: part.url ?? undefined,
        title: part.title ?? undefined,
        mediaType: part.mediaType ?? undefined,
        filename: part.filename ?? undefined,
        agentState: part.agentState ?? undefined,
        ingestionMode: part.document?.ingestionMode ?? undefined,
        extractionStatus: part.document?.extractionStatus ?? undefined,
        document: part.document
          ? {
              id: part.document.id,
              signedDownloadUrl: part.document.signedDownloadUrl ?? undefined,
              thumbnailSignedUrl: part.document.thumbnailSignedUrl ?? undefined,
              sizeBytes: part.document.sizeBytes ?? undefined,
              pageCount: part.document.pageCount ?? undefined,
            }
          : undefined,
      };
    }

    if (part.type === "step_start") {
      return {
        id,
        type: "step",
        stepIndex: part.stepIndex ?? undefined,
        agentState: part.agentState ?? undefined,
      };
    }

    return {
      id,
      type: "text",
      text: part.text ?? "",
      agentState: part.agentState ?? undefined,
    };
  }

  private mapHistoryMessage(message: ScoutConversationMessage): ScoutChatMessage | null {
    const parts = message.parts.map((part) => this.mapHistoryPart(part));
    const role = normalizeRole(message.role);
    if (!role) {
      return null;
    }
    // For assistant messages we key on `scout-assistant-${requestId}` so a
    // partial assistant message hydrated from history collides — and merges —
    // with the synthesized live id `applyStreamEvent` produces from the same
    // requestId. Without this collision, post-refresh-mid-stream would render
    // the same logical reply twice (once from history, once from the live
    // stream's replayed events). Falls back to the DB UUID for assistant rows
    // persisted before requestId existed; those can't have a live stream
    // anymore so the dup risk is structurally zero.
    const id =
      role === "assistant" && message.requestId
        ? `scout-assistant-${message.requestId}`
        : message.id;
    const mapped: ScoutChatMessage = {
      id,
      clientInstanceId: message.id,
      persistedMessageId: message.id,
      role,
      content: parts.map(textFromPart).filter(Boolean).join(""),
      parts,
      isStreaming: false,
      seq: message.seq,
      createdAt: message.createdAt,
      updatedAt: message.updatedAt,
      turnId: message.turnId ?? undefined,
      frontMessageId: message.frontMessageId ?? null,
      feedback: message.feedback
        ? {
            id: message.feedback.id,
            rating: message.feedback.rating,
            feedback: message.feedback.feedback ?? null,
            submittedByEmail: message.feedback.submittedByEmail ?? null,
            createdAt: message.feedback.createdAt,
            updatedAt: message.feedback.updatedAt,
          }
        : undefined,
    };

    if (mapped.role !== "user") {
      return mapped;
    }

    return {
      ...mapped,
      content: mapped.content || message.parts.find((part) => part.type === "text")?.text || "",
    };
  }

  private normalizeToolState(value: string | null | undefined): ScoutToolState {
    if (
      value === "input_streaming" ||
      value === "input_available" ||
      value === "output_available" ||
      value === "output_error"
    ) {
      return value;
    }

    return "input_available";
  }

  private upsertPart(parts: ScoutMessagePart[], nextPart: ScoutMessagePart): ScoutMessagePart[] {
    const nextParts = parts.map((part) => (part.id === nextPart.id ? nextPart : part));
    return nextParts.some((part) => part.id === nextPart.id) ? nextParts : [...parts, nextPart];
  }

  /**
   * Resolve a stable, per-block client id for a text or reasoning stream
   * event. The contract:
   *
   *   - Every *-start opens a NEW client block and fabricates a fresh id of
   *     the form `scout-{requestId}-{kind}-{seq}`. If the SDK supplied a raw
   *     id, we record `rawId → fabricatedId` so subsequent *-delta / *-end
   *     events with that raw id can resolve to the same fabricated block.
   *   - *-delta and *-end without a raw id (or with an id we never saw a
   *     start for) attach to the most-recently-opened block of that kind.
   *   - On *-end, drop the `rawId → fabricatedId` mapping so a later *-start
   *     with the SAME raw id will fabricate a fresh id rather than reuse
   *     the previous block. This is the key behavior change: it mirrors how
   *     the server's `ScoutConversationMaterializer` deletes its buffer
   *     entry on flush, which is why DB rows stay distinct even when the
   *     upstream SDK reuses ids across blocks.
   *
   * Without this, an SDK that reuses `id=A` for a later text block would
   * cause the client to merge the second block into the first via
   * `upsertPart`, pinning any tool calls that arrived between them to the
   * end of the parts array.
   */
  private resolveStreamBlockId(
    eventName: string,
    requestId: string,
    kind: "text" | "reasoning",
    part: Record<string, unknown>
  ): string {
    const rawId = readOptionalString(part, "id");
    const isStartEvent = eventName === `${kind}-start`;
    const isEndEvent = eventName === `${kind}-end`;

    const state = this.getOrCreateStreamBlockState(requestId);
    const openMap = state.openByRawId[kind];

    if (isStartEvent) {
      const fabricated = this.fabricateStreamBlockId(requestId, kind);
      if (rawId) {
        openMap.set(rawId, fabricated);
      }
      state.latestId[kind] = fabricated;
      return fabricated;
    }

    // For delta / end: prefer the explicit raw-id mapping, then fall back
    // to the latest opened block of this kind for that turn. If neither
    // exists (out-of-order or dropped *-start), fabricate one implicitly
    // so the delta still lands somewhere predictable.
    let fabricated: string | undefined;
    if (rawId) {
      fabricated = openMap.get(rawId);
    }
    if (!fabricated) {
      fabricated = state.latestId[kind] ?? undefined;
    }
    if (!fabricated) {
      fabricated = this.fabricateStreamBlockId(requestId, kind);
      if (rawId) {
        openMap.set(rawId, fabricated);
      }
      state.latestId[kind] = fabricated;
    }

    if (isEndEvent && rawId) {
      // The block is closing — drop the raw-id mapping so a later *-start
      // with the same raw id starts a fresh block. We deliberately do NOT
      // clear `latestId[kind]`; if a stray delta arrives without a raw id
      // after the end (rare but seen), it still resolves to the most
      // recently closed block instead of fabricating yet another.
      openMap.delete(rawId);
    }

    return fabricated;
  }

  private fabricateStreamBlockId(requestId: string, kind: "text" | "reasoning"): string {
    const state = this.getOrCreateStreamBlockState(requestId);
    const seq = state.nextSeq[kind]++;
    return `scout-${requestId}-${kind}-${seq}`;
  }

  private getOrCreateStreamBlockState(requestId: string) {
    let state = this.streamBlockState.get(requestId);
    if (!state) {
      state = {
        latestId: { text: null, reasoning: null },
        nextSeq: { text: 0, reasoning: 0 },
        openByRawId: { text: new Map(), reasoning: new Map() },
      };
      this.streamBlockState.set(requestId, state);
    }
    return state;
  }

  private getOrCreateLiveAssistantMessage(input: {
    activeTurn: ScoutActiveTurn | undefined;
    commandId: string | undefined;
    messages: ScoutChatMessage[];
    requestId: string;
  }): ScoutChatMessage {
    const existing = input.messages.find(
      (message) => message.id === `scout-assistant-${input.requestId}`
    );
    if (existing) {
      return existing;
    }

    const commandAssistant =
      input.commandId && input.commandId === input.activeTurn?.commandId
        ? input.messages.find(
            (message) => message.id === `scout-assistant-${input.activeTurn?.requestId}`
          )
        : undefined;
    if (commandAssistant?.isStreaming === true) {
      return commandAssistant;
    }

    const optimisticAssistant = input.messages.find(
      (message) =>
        message.role === "assistant" &&
        message.isStreaming &&
        (message.parts ?? []).some((part) => this.isOptimisticThinkingPart(part))
    );
    if (optimisticAssistant) {
      return optimisticAssistant;
    }

    return {
      id: `scout-assistant-${input.requestId}`,
      role: "assistant",
      content: "",
      parts: [],
      isStreaming: true,
    };
  }

  private isOptimisticThinkingPart(part: ScoutMessagePart) {
    return part.type === "text" && part.agentState === "thinking" && part.id.includes("-thinking");
  }

  // Whether a message has anything the chat actually renders. Mirrors the
  // "stale empty assistant" check in ChatChrome: reasoning/step parts are
  // hidden from customers and text parts must be non-empty.
  private messageHasRenderableContent(message: ScoutChatMessage): boolean {
    return (message.parts ?? []).some((part) => {
      if (part.type === "reasoning" || part.type === "step") {
        return false;
      }
      if (part.type === "text") {
        return Boolean(part.text?.trim());
      }
      return true;
    });
  }

  private removeOptimisticThinkingParts(message: ScoutChatMessage): ScoutChatMessage {
    const parts = message.parts ?? [];
    const nextParts = parts.filter((part) => !this.isOptimisticThinkingPart(part));

    if (nextParts.length === parts.length) {
      return message;
    }

    return this.recomputeMessageContent({
      ...message,
      parts: nextParts,
    });
  }

  private isRunningTurn(turn: ScoutActiveTurn | undefined): turn is ScoutActiveTurn {
    return turn?.status === "starting" || turn?.status === "streaming";
  }

  private isTerminalTurn(turn: ScoutActiveTurn | undefined): turn is ScoutActiveTurn {
    return turn?.status === "finished" || turn?.status === "aborted" || turn?.status === "error";
  }

  private isMessageFromTurn(message: ScoutChatMessage, turn: ScoutActiveTurn | undefined) {
    if (!turn?.startedAtMs || !message.createdAt) {
      return false;
    }

    const messageCreatedAtMs = Date.parse(message.createdAt);
    return Number.isFinite(messageCreatedAtMs) && messageCreatedAtMs >= turn.startedAtMs;
  }

  private shouldIgnoreMismatchedRunningTurnEvent(input: {
    activeTurn: ScoutActiveTurn | undefined;
    commandId: string | undefined;
    event: StreamEvent;
    previousMessages: ScoutChatMessage[];
    requestId: string;
  }) {
    const activeTurn = input.activeTurn;
    if (!this.isRunningTurn(activeTurn) || activeTurn.requestId === input.requestId) {
      return false;
    }

    if (activeTurn.commandId && input.commandId === activeTurn.commandId) {
      return false;
    }

    const matchingAssistantMessage = input.previousMessages.find(
      (message) => message.id === `scout-assistant-${input.requestId}`
    );
    if (matchingAssistantMessage && !matchingAssistantMessage.isStreaming) {
      return true;
    }

    if (isTerminalStreamEvent(input.event)) {
      return true;
    }

    const activeAssistantMessage = input.previousMessages.find(
      (message) => message.id === `scout-assistant-${activeTurn.requestId}`
    );
    const hasSettledAssistantHistory = input.previousMessages.some(
      (message) => message.role === "assistant" && !message.isStreaming
    );
    const eventTimestampMs = readStreamEventTimestampMs(input.event.id);
    const isEventAfterTurnStarted =
      eventTimestampMs !== undefined &&
      activeTurn.startedAtMs !== undefined &&
      eventTimestampMs >= activeTurn.startedAtMs;
    const canRetargetOptimisticAssistant =
      (!hasSettledAssistantHistory || isEventAfterTurnStarted) &&
      activeAssistantMessage?.isStreaming === true &&
      (activeAssistantMessage.parts ?? []).some((part) => this.isOptimisticThinkingPart(part));

    return !canRetargetOptimisticAssistant;
  }

  private replaceMessage(
    messages: ScoutChatMessage[],
    nextMessage: ScoutChatMessage,
    previousMessageId = nextMessage.id
  ) {
    let didReplace = false;
    let didChange = false;
    const nextMessages: ScoutChatMessage[] = [];

    for (const message of messages) {
      if (message.id === nextMessage.id || message.id === previousMessageId) {
        if (!didReplace) {
          nextMessages.push(nextMessage);
          didReplace = true;
          didChange = didChange || message !== nextMessage || message.id !== nextMessage.id;
        } else {
          didChange = true;
        }
        continue;
      }

      nextMessages.push(message);
    }

    if (!didReplace) {
      return [...messages, nextMessage];
    }

    return didChange ? nextMessages : messages;
  }

  private resolveTurnStatus(event: StreamEvent): ScoutTurnStatus {
    // A `finish` with `finishReason: "tool-calls"` is the SDK ending a single
    // roundtrip with more tool-calling to come — NOT end-of-turn. See
    // `isTerminalStreamEvent` for the shared predicate.
    if (event.event === "finish") {
      return isTerminalStreamEvent(event) ? "finished" : "streaming";
    }

    if (event.event === "error") {
      return "error";
    }

    if (event.event === "abort" || event.event === "turn_aborted") {
      return "aborted";
    }

    return "streaming";
  }

  private updateMessageForStreamEvent(
    message: ScoutChatMessage,
    requestId: string,
    event: StreamEvent
  ): ScoutChatMessage {
    const part = readStreamPart(event);
    if (!part) {
      return {
        ...message,
        isStreaming: !isTerminalStreamEvent(event),
      };
    }

    // Only drop the optimistic "Thinking..." placeholder when we're about to
    // add something the user will actually see (a text or tool part).
    const producesVisiblePart = event.event.startsWith("text-") || event.event.startsWith("tool-");
    const messageForEvent = producesVisiblePart
      ? this.removeOptimisticThinkingParts(message)
      : message;

    if (
      event.event === "text-start" ||
      event.event === "text-delta" ||
      event.event === "text-end"
    ) {
      const id = this.resolveStreamBlockId(event.event, requestId, "text", part);
      const existingPart = (messageForEvent.parts ?? []).find((candidate) => candidate.id === id);
      const existingText = existingPart?.type === "text" ? existingPart.text : "";
      const nextText =
        event.event === "text-delta" ? `${existingText}${readTextDelta(part)}` : existingText;
      const nextPart: ScoutTextPart = {
        id,
        type: "text",
        text: nextText,
        agentState: event.event === "text-end" ? "idle" : "streaming",
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: event.event !== "text-end",
      });
    }

    if (
      event.event === "reasoning-start" ||
      event.event === "reasoning-delta" ||
      event.event === "reasoning-end"
    ) {
      const id = this.resolveStreamBlockId(event.event, requestId, "reasoning", part);
      const existingPart = (messageForEvent.parts ?? []).find((candidate) => candidate.id === id);
      const existingText = existingPart?.type === "reasoning" ? existingPart.text : "";
      const nextText =
        event.event === "reasoning-delta"
          ? `${existingText}${readReasoningDelta(part)}`
          : existingText;
      const nextPart: ScoutReasoningPart = {
        id,
        type: "reasoning",
        text: nextText,
        agentState: event.event === "reasoning-end" ? "idle" : "thinking",
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: event.event !== "reasoning-end",
      });
    }

    if (
      event.event === "tool-input-start" ||
      event.event === "tool-input-delta" ||
      event.event === "tool-input-end" ||
      event.event === "tool-call" ||
      event.event === "tool-result" ||
      event.event === "tool-error"
    ) {
      const toolCallId = resolveToolCallId(requestId, part);
      const existingPart = (messageForEvent.parts ?? []).find(
        (candidate) => candidate.id === toolCallId
      );
      const existingTool = existingPart?.type === "tool" ? existingPart : undefined;
      const inputDelta = readOptionalString(part, "delta") ?? "";
      const existingInputText =
        typeof existingTool?.toolInput === "string" ? existingTool.toolInput : "";
      const nextInputFromDelta =
        inputDelta.length > 0
          ? parseJsonIfPossible(`${existingInputText}${inputDelta}`)
          : undefined;
      const toolState = this.resolveToolStateForEvent(event.event, existingTool?.toolState);
      const nextPart: ScoutToolPart = {
        id: toolCallId,
        type: "tool",
        toolName: readOptionalString(part, "toolName") ?? existingTool?.toolName ?? "unknown_tool",
        toolDisplayName:
          readOptionalString(part, "toolDisplayName") ?? existingTool?.toolDisplayName,
        toolCallId,
        toolState,
        toolInput:
          readJsonLikeValue(part, ["toolInput", "input", "args"]) ??
          nextInputFromDelta ??
          existingTool?.toolInput,
        toolOutput:
          readJsonLikeValue(part, ["toolOutput", "output", "result"]) ?? existingTool?.toolOutput,
        toolError: readOptionalString(part, "error") ?? existingTool?.toolError,
        agentState:
          event.event === "tool-result" || event.event === "tool-error" ? "idle" : "tool_calling",
        // Pin to the moment we first observed the call. Later updates (delta,
        // result) keep the original timestamp so the relative-time display
        // doesn't reset to "just now" every time the tool emits another
        // event.
        createdAt: existingTool?.createdAt ?? new Date().toISOString(),
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: event.event !== "tool-result" && event.event !== "tool-error",
      });
    }

    if (event.event === "source") {
      const id = resolveStreamPartId(requestId, "source", part);
      const nextPart: ScoutSourcePart = {
        id,
        type: "source",
        sourceId: readOptionalString(part, "sourceId") ?? readOptionalString(part, "id"),
        url: readOptionalString(part, "url"),
        title: readOptionalString(part, "title"),
        mediaType: readOptionalString(part, "mediaType") ?? readOptionalString(part, "mimeType"),
        filename: readOptionalString(part, "filename"),
        agentState: "streaming",
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: true,
      });
    }

    if (event.event === "file") {
      const file = isRecord(part.file) ? part.file : undefined;
      const id = resolveStreamPartId(requestId, "file", part);
      const nextPart: ScoutFilePart = {
        id,
        type: "file",
        url:
          readOptionalString(part, "url") ?? (file ? readOptionalString(file, "url") : undefined),
        title:
          readOptionalString(part, "title") ??
          (file ? readOptionalString(file, "title") : undefined),
        mediaType:
          readOptionalString(part, "mediaType") ??
          readOptionalString(part, "mimeType") ??
          (file ? readOptionalString(file, "mediaType") : undefined) ??
          (file ? readOptionalString(file, "mimeType") : undefined),
        filename:
          readOptionalString(part, "filename") ??
          (file ? readOptionalString(file, "filename") : undefined),
        agentState: "streaming",
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: true,
      });
    }

    if (event.event === "start-step" || event.event === "finish-step") {
      const data = readStreamDataRecord(event);
      const stepIndex = data ? readOptionalNumber(data, "step") : undefined;
      const nextPart: ScoutStepPart = {
        id: resolveStreamPartId(requestId, "step", part),
        type: "step",
        stepIndex,
        agentState: event.event === "finish-step" ? "idle" : "streaming",
      };

      return this.recomputeMessageContent({
        ...messageForEvent,
        parts: this.upsertPart(messageForEvent.parts ?? [], nextPart),
        isStreaming: true,
      });
    }

    const nextIsStreaming = !isTerminalStreamEvent(event);
    if (messageForEvent.isStreaming === nextIsStreaming) {
      return messageForEvent;
    }

    return {
      ...messageForEvent,
      isStreaming: nextIsStreaming,
    };
  }

  private resolveToolStateForEvent(
    eventName: string,
    existingToolState: ScoutToolState | undefined
  ): ScoutToolState {
    if (eventName === "tool-input-start" || eventName === "tool-input-delta") {
      return "input_streaming";
    }

    if (eventName === "tool-result") {
      return "output_available";
    }

    if (eventName === "tool-error") {
      return "output_error";
    }

    return existingToolState ?? "input_available";
  }

  private setSnapshot(nextSnapshot: ScoutConversationSnapshot) {
    this.snapshot = nextSnapshot;

    if (this.batchDepth > 0) {
      this.hasPendingNotification = true;
      return;
    }

    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const scoutConversationStore = new ScoutConversationStore();
